import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import ProductLineCounts from "./ProductLineCounts.graphql";
import { PRODUCT_LINE_PARAMETER_ID } from "../../constants";
import { productSearch } from "../../modules/api";

interface IProductLineCount {
    productLineID: string
    productCount: number
}

interface IPropertyValueFacet {
    value: unknown
    count: number
}

/**
 * Live product counts per product line for a brand, read from the search index
 * via the `propertyValues` facet over the product-line parameter. `brandID` must
 * be the brand's ObjectId (matches the index `brand.brandID`). Lines with zero
 * products are absent from the result.
 */
export default function useProductLineCounts(brandID: string) {
    const result = useQuery<{
        searchProducts: { facets: { propertyValues: IPropertyValueFacet[] } }
    }>(
        gql(ProductLineCounts),
        {
            client: productSearch,
            skip: !brandID,
            variables: {
                filter: { brandIDs: [brandID] },
                parameterIDs: [PRODUCT_LINE_PARAMETER_ID],
            },
        },
    );

    const productLineCounts = useMemo<IProductLineCount[]>(
        () => (result.data?.searchProducts.facets.propertyValues ?? [])
            .filter((facet) => facet.value !== null && facet.value !== undefined)
            .map((facet) => ({ productLineID: String(facet.value), productCount: facet.count })),
        [result.data],
    );

    return {
        ...result,
        data: result.data ? { productLineCounts } : undefined,
    };
}
